import { ConnectIcon } from "@minket/brand/icons/connect";
import { Button } from "@minket/ui/components/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import {
  ConnectMailboxDialog,
  type ConnectState,
  type SmtpBody,
} from "@/components/mailboxes/dialogs";
import { providerLabels, readCallback, returnUrl } from "@/components/mailboxes/model";
import { useMarkStepDone } from "@/components/onboarding/mark-done";
import { StepPage } from "@/components/onboarding/step-page";
import { UnavailableAction } from "@/components/page/action";
import { Notice } from "@/components/page/states";
import { describeError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/onboarding/mailboxes")({
  component: MailboxesStep,
});

const connectAction = { label: "Connect mailbox", icon: ConnectIcon };

function MailboxesStep() {
  return (
    <StepPage
      path="/onboarding/mailboxes"
      intro="Connect a Gmail, Microsoft 365 or SMTP mailbox to send as a person; copies land in its Sent folder."
      asks={[
        {
          title: "A provider",
          text: "Gmail and Microsoft 365 connect with OAuth; any other mailbox connects with SMTP, and with IMAP for Sent copies.",
        },
        {
          title: "Consent",
          text: "Gmail and Microsoft ask you to allow sending; the token is stored encrypted.",
        },
      ]}
      action={connectAction}
      control={<StepControl />}
    />
  );
}

/** Connect the first mailbox; once one is connected the step is done. */
function StepControl() {
  const { project } = useWorkspace();
  const canManage = useCan("mailboxes.manage");
  const projectId = project?.id ?? "";
  const queryClient = useQueryClient();
  const list = useQuery({ ...apiQuery(endpoints.mailboxes, { projectId }), enabled: !!project });
  const search = useLocation({ select: (location) => location.search as Record<string, unknown> });
  const [callback] = useState(() => readCallback(search));
  const [dialog, setDialog] = useState<ConnectState | null>(null);
  const markDone = useMarkStepDone("mailboxes");
  const connected = list.data?.data.find((mailbox) => mailbox.status === "connected");
  useEffect(() => {
    if (connected) markDone();
  }, [connected, markDone]);

  const connect = useMutation({
    mutationFn: (body: { provider: "gmail" | "microsoft"; return_url: string } | SmtpBody) =>
      apiRequest(endpoints.connectMailbox, { projectId }, { body }),
    onSuccess: (result) => {
      if (result.object === "oauth") {
        window.location.assign(result.url);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.mailboxes, { projectId }) });
      setDialog(null);
      notify("success", `${result.address} connected.`);
    },
  });

  const unavailable = (reason: string) => (
    <UnavailableAction {...connectAction} reason={reason} align="start" />
  );
  if (!project) return unavailable("Choose a project first");
  if (list.isPending) return unavailable("Loading mailboxes");
  if (list.isError) {
    return unavailable(describeError(list.error).message);
  }
  if (!canManage) {
    return unavailable("Viewers cannot connect mailboxes");
  }

  return (
    <div className="grid max-w-md min-w-0 gap-2">
      <Button
        className="w-fit"
        onClick={() => {
          connect.reset();
          setDialog({ start: "choose" });
        }}
      >
        <ConnectIcon className="size-5" />
        {connected ? "Connect another mailbox" : "Connect mailbox"}
      </Button>
      <div role="status" className="grid gap-1">
        {callback ? <Notice tone={callback.tone}>{callback.message}</Notice> : null}
        {connected ? (
          <Notice tone="success">
            <span className="type-data">{connected.address}</span> is connected through{" "}
            {providerLabels[connected.provider]}.
          </Notice>
        ) : null}
      </div>
      {dialog ? (
        <Suspense fallback={null}>
          <ConnectMailboxDialog
            state={dialog}
            pending={connect.isPending}
            error={connect.error}
            onOauth={(provider) =>
              connect.mutate({ provider, return_url: returnUrl("/onboarding/mailboxes") })
            }
            onSmtp={(body) => connect.mutate(body)}
            onClose={() => setDialog(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
